import type { GridApi, IRowNode, ValueFormatterParams } from "ag-grid-community"

import { MAX_TIERS_PER_GROUP } from "./constants"
import type { PricingGridRow } from "./types"

import { CURRENCY_RENDER_PRECISION, UOM_RENDER_PRECISION } from "@/constants/formats"
import { PriceChangeTableVariant } from "@/screens/Pricing/constants"
import { formatNumber } from "@/utils/number"

const numberFormatter =
  ({
    prefix = "",
    suffix = "",
    precision = UOM_RENDER_PRECISION,
  }: {
    prefix?: string
    suffix?: string
    precision?: number
  }) =>
  ({ value }: ValueFormatterParams<PricingGridRow>): string =>
    value === null ? "" : `${prefix}${formatNumber(value as number, precision, precision)}${suffix}`

const uomSuffixFormatter = (uomName: string, precision = UOM_RENDER_PRECISION) =>
  numberFormatter({
    suffix: ` ${uomName}`,
    precision,
  })

export const FORMATTERS = {
  financial: numberFormatter({ prefix: "$", precision: CURRENCY_RENDER_PRECISION }),
  uomSuffix: uomSuffixFormatter,
  percentSuffix: numberFormatter({ suffix: "%" }),
} as const

export interface RowActionsState {
  isAddDisabled: boolean
  isNewRow: boolean
  clearPriceTierLabel: string
  isUndoAction: boolean
}

export const getRowActionsState = (
  node: IRowNode<PricingGridRow>,
  api: GridApi<PricingGridRow>,
  isRowNew: (row: PricingGridRow) => boolean,
  variant?: PriceChangeTableVariant
): RowActionsState => {
  const itemId = node.data?.item.priceChangeItemId
  let groupTierCount = 0
  api.forEachNode((n) => {
    if (n.data?.item.priceChangeItemId === itemId) groupTierCount++
  })
  const isAddDisabled = groupTierCount >= MAX_TIERS_PER_GROUP
  const isNewRow = node.data ? isRowNew(node.data) : false
  const isUndoAction = !isNewRow && variant === PriceChangeTableVariant.ReviewSubmission
  const clearPriceTierLabel = isNewRow ? "Remove Price Tier" : isUndoAction ? "Undo changes" : "Clear Price Tier"

  return { isAddDisabled, isNewRow, clearPriceTierLabel, isUndoAction }
}

export const refreshRowsInPlace = (rowNodes: IRowNode<PricingGridRow>[]): void => {
  for (const rowNode of rowNodes) {
    if (rowNode.data) rowNode.updateData(rowNode.data)
  }
}
